import { useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { getServiceName, isTaskActive, taskStatusByCode } from 'entities';

import { Button, Card, IconButton, Row, Table, Tag } from 'components/Blocks';
import { UserAvatarGroup } from 'components/UserAvatar';
import { ExpandIcon } from 'components/Icons';
import { EntityStatusTag } from 'components/Entities';

import { TaskOptions } from '../../TaskOptions';
import { ExpectedDate, ServiceOutsourcedTag } from 'pages/Projects/components';

import { useProjectProfile } from '../../context';

import { getFirstAndLastName, getFirstWord } from 'utils/text';

export function TaskTable() {
  const { t } = useLang();
  const { project, openTask } = useProjectProfile();

  const sortedTasks = useMemo(() => {
    return project.tasks
      .slice()
      .sort(({ expectedDate: expectedA }, { expectedDate: expectedB }) => {
        const defaultDate = '2040-04-22T12:00:00.000';

        const [dateA, dateB] = [expectedA, expectedB].map((date) => new Date(date || defaultDate));

        return Number(dateA) - Number(dateB);
      });
  }, [project]);

  return (
    <Card className="scrollbar overflow-y-auto">
      <Table
        items={sortedTasks}
        itemsPerPage={15}
        uniqueKey="id"
        columns={[
          {
            label: t('name'),
            property: 'name',
            search: true,
            sort: true,
            render: (task) => (
              <Button
                variant="link"
                className="font-normal"
                color="gray"
                onClick={() => openTask(task.id)}
              >
                {getServiceName(task.serviceConfigId, task)}
              </Button>
            ),
          },
          {
            label: t('owner'),
            filter: true,
            getTextValue: ({ owner }) => owner.name,
            render: ({ owner, subOwner }) => (
              <Row gap="2">
                <UserAvatarGroup
                  users={[owner, subOwner]}
                  size={32}
                  className="justify-start"
                  avatarClassName="text-sm"
                  showNameOnHover={false}
                />

                <p className="text-center">
                  {subOwner
                    ? `${getFirstWord(owner.name)} & ${getFirstWord(subOwner.name)}`
                    : getFirstAndLastName(owner.name)}
                </p>
              </Row>
            ),
          },
          {
            label: t('tags'),

            hide: sortedTasks.every(
              (t) => !t.tags.length && !t.outsourced && !t.activeRecruitments,
            ),

            render: ({ tags, outsourced, activeRecruitments }) => (
              <Row gap="1" flexWrap="wrap">
                {outsourced && <ServiceOutsourcedTag size="medium" />}

                {!!activeRecruitments && (
                  <Tag size="medium" name={t('recruiting')} color="success" />
                )}

                {tags.map(({ tag }) => (
                  <Tag size="medium" key={tag.id} {...tag} />
                ))}
              </Row>
            ),
          },
          {
            label: t('due-date'),
            sort: true,
            getTextValue: ({ expectedDate }) => expectedDate ?? '-',
            render: ({ expectedDate, status }) => (
              <ExpectedDate date={expectedDate} isActive={isTaskActive(project, status)} />
            ),
          },
          {
            label: t('status'),
            filter: true,
            getTextValue: ({ status }) => taskStatusByCode[status].getLabel(t),
            render: ({ status }) => (
              <EntityStatusTag
                status={status}
                target="task"
                size="medium"
                className="w-full max-w-44 justify-self-end"
              />
            ),
          },
          {
            render: (task) => (
              <Row gap="1" justifyContent="flex-end">
                <IconButton
                  className="ml-1"
                  tooltipContent={t('open') as string}
                  onClick={() => openTask(task.id)}
                >
                  <ExpandIcon />
                </IconButton>

                <TaskOptions task={task} />
              </Row>
            ),
          },
        ]}
      />
    </Card>
  );
}
